/// <amd-module name="Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/AnalyticsSprintBurndownSettingsManager" />
import { Iteration } from "Analytics/Boards/BoardsQueries/IterationsQuery";
import { WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import * as TfsWidgetContext from "Tfs/Widget/Context";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import {
    DateSampleInterval,
    DayOfWeek,
    daysBetweenDates,
    parseIsoDateStringInLocalTime,
    toLocalDateIsoString
} from "WidgetCommon/DateUtils/DateUtils";
import { AggregationMode, TeamScope } from "WidgetCommon/ModernWidgetTypes/CommonConfigurationTypes";
import { ModefulValueSetting } from "WidgetCommon/ModernWidgetTypes/ModefulValueSetting";
import { SettingsManagerBase, WidgetSettingsManagerProps } from "WidgetCommon/ModernWidgetTypes/SettingsManagerBase";
import * as Resources from "Widgets/AnalyticsSprintBurndown/Resources";
import { AggregationSettings, BurndownSettings, DateSampleMode } from "Widgets/Burndown/BurndownWidgetComponents/BurndownSettings";
import { AnalyticsSprintBurndownConstants } from "./AnalyticsSprintBurndownConstants";
import { AnalyticsSprintBurndownSettings, SprintBurndownTimePeriod } from "./AnalyticsSprintBurndownSettings";
import {
    getIterations,
    isSymbolicCurrentIteration,
    populateSymbolicCurrentIteration,
    selectActualCurrentIteration
} from "./ConfigQueries/IterationsHelper";

export class AnalyticsSprintBurndownSettingsManager extends SettingsManagerBase<AnalyticsSprintBurndownSettings> {
    private static isValidDate(dateString: string): boolean {
        return dateString != null && !isNaN(new Date(dateString).getTime());
    }

    public static settingsAreValid(settings: AnalyticsSprintBurndownSettings): boolean {
        const iterationDatesValid =
            settings != null &&
            settings.timePeriodConfiguration != null &&
            AnalyticsSprintBurndownSettingsManager.isValidDate(settings.timePeriodConfiguration.startDate) &&
            AnalyticsSprintBurndownSettingsManager.isValidDate(settings.timePeriodConfiguration.endDate);

        if (!iterationDatesValid) {
            throw Error(Resources.IterationDatesUnconfiguredMessage);
        }

        const iterationDateRangesValid =
            settings != null &&
            settings.timePeriodConfiguration != null &&
            daysBetweenDates(
                parseIsoDateStringInLocalTime(settings.timePeriodConfiguration.startDate),
                parseIsoDateStringInLocalTime(settings.timePeriodConfiguration.endDate)
            ) <= 180;
        const iterationDatesInOrder =
            settings != null &&
            settings.timePeriodConfiguration != null &&
            parseIsoDateStringInLocalTime(settings.timePeriodConfiguration.startDate).getTime() <
            parseIsoDateStringInLocalTime(settings.timePeriodConfiguration.endDate).getTime();

        const settingsAreValid =
            settings != null &&
            settings.aggregation != null &&
            settings.aggregation.identifier != null &&
            (settings.aggregation.identifier === AggregationMode.Count ||
                (settings.aggregation.identifier === AggregationMode.Sum && settings.aggregation.settings != null)) &&
            settings.iterationId != null &&
            settings.iterationId != "" &&
            settings.team != null &&
            settings.team.projectId != null &&
            settings.team.teamId != null &&
            iterationDateRangesValid &&
            iterationDatesInOrder;

        return settingsAreValid;
    }
    public constructor(private pageContext: IVssPageContext, private teamContext: WidgetTeamContext) {
        super();
    }

    public getFeatureName() {
        return AnalyticsSprintBurndownConstants.featureName;
    }

    /** This is a Burndown proprietary pattern, which duplicates the common widget settings path. */
    public async hasSatisfactoryConfiguration(widgetProps: WidgetSettingsManagerProps): Promise<boolean> {
        return this.isCorrectlyConfigured(widgetProps);
    }

    public isCorrectlyConfigured(props: WidgetSettingsManagerProps): boolean {
        /*
         * If there's *anything* in data, we don't have a default configuration.
         * We consider null the same as undefined: Unconfigured.
         */
        if (!super.isConfigured(props)) {
            return false;
        }

        const settings = JSON.parse(props.settings!) as AnalyticsSprintBurndownSettings;
        return AnalyticsSprintBurndownSettingsManager.settingsAreValid(settings);
    }

    /**
     * Provides promise to perform neccessary legwork to produce default Velocity Settings, if possible.
     */
    public async generateDefaultSettings(): Promise<AnalyticsSprintBurndownSettings> {
        const context = this.packContext(this.teamContext);

        // Step 2 - Choose WIT Option
        const workItemTypeFilter = {
            identifier: "BacklogCategory",
            settings: await getTaskBacklogCategoryReferenceName(this.pageContext, this.teamContext)
        };

        // Step 4 - Get info about active and available Iteration Options (No-op, selection is Handled by parent)
        const iterations = await getIterations(this.pageContext, context.projectId, context.teamId);
        // Note: Teams can have no available iterations
        let activeIteration = populateSymbolicCurrentIteration(iterations);
        if (activeIteration == null) {
            activeIteration = { IterationSK: "", IterationPath: "" } as Iteration;
        }

        const startDate = new Date((activeIteration != null && activeIteration.StartDateTimeOffset) || "");
        const endDate = new Date((activeIteration != null && activeIteration.EndDateTimeOffset) || "");
        const showNonWorkingDays = false;

        return Promise.resolve(
            assembleBurndownReportSettings(
                context.projectId,
                context.teamId,
                workItemTypeFilter,
                undefined, // Aggregation settings can be undefined at this stage as defaults are applied later when we assemble settings below
                startDate,
                endDate,
                activeIteration,
                showNonWorkingDays
            )
        );
    }

    /** Collects  neccessary initial information for querying for default settings. Decoupled from main method for testability. */
    private packContext(teamContext: WidgetTeamContext): TeamScope {
        return {
            teamId: teamContext.id,
            projectId: TfsWidgetContext.getProject(this.pageContext).id
        };
    }
}

export function assembleTimePeriodConfiguration(startDate: Date | undefined, endDate: Date | undefined): SprintBurndownTimePeriod {
    const fallbackDateString = ""; // For if iteration was undefined.
    return {
        startDate: startDate != null && !isNaN(startDate.getTime()) ? toLocalDateIsoString(startDate) : fallbackDateString,
        endDate: endDate != null && !isNaN(endDate.getTime()) ? toLocalDateIsoString(endDate) : fallbackDateString
    };
}

/** A narrow definition of the aspect of the Backlog Configuration required by Sprint Burndown Widget. */
interface BacklogConfigPayload {
    taskBacklog: {
        /** The friendly name */
        name: string;
        /** The reference name */
        id: string;
    };
}

/**  Get the task backlog name from the boards service for the current project */
export async function getTaskBacklogCategoryReferenceName(pageContext: IVssPageContext, teamContext: WidgetTeamContext): Promise<string> {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const backlogConfigurationData = (await contributionService.getDataAsync(
        "ms.vss-dashboards-web.backlog-configuration-for-team-data-provider",
        { teamId: teamContext.id }
    )) as BacklogConfigPayload;
    const backlogCategoryReferenceName = backlogConfigurationData.taskBacklog.id;
    return backlogCategoryReferenceName;
}

/** Path for generating Burndown Settings
 */
export function assembleBurndownReportSettings(
    projectId: string,
    teamId: string,
    workItemTypeFilter: ModefulValueSetting<string, string> | undefined,
    aggregationSettings: AggregationSettings | undefined,
    startDate: Date | undefined,
    endDate: Date | undefined,
    iteration: { IterationSK: string; IterationPath: string } | undefined,
    showNonWorkingDays: boolean
): AnalyticsSprintBurndownSettings {
    const settings: AnalyticsSprintBurndownSettings = {
        /** Spec required initial default values */
        totalScopeTrendlineEnabled: true, // By default total scope line is shown, but the widget will ignore at render time if the user selects "remaining work".
        completedWorkEnabled: false,
        stackByWorkItemTypeEnabled: false,
        showNonWorkingDays: showNonWorkingDays,
        showResolvedItemsAsCompletedEnabled: false,
        aggregation: aggregationSettings
            ? aggregationSettings
            : {
                  identifier: AggregationMode.Sum,
                  settings: "Microsoft.VSTS.Scheduling.RemainingWork"
              },
        workItemTypeFilter: workItemTypeFilter,
        timePeriodConfiguration: assembleTimePeriodConfiguration(startDate, endDate),
        team: {
            teamId: teamId,
            projectId: projectId
        },
        iterationId: (iteration != null && iteration.IterationSK) || "",
        iterationPath: (iteration != null && iteration.IterationPath) || "",

        isMinimalChart: false,
        isLightboxChart: false,
        isLegacy: false
    } as AnalyticsSprintBurndownSettings;

    return settings;
}

export function convertSprintBurndownToBurndownSettings(settings: AnalyticsSprintBurndownSettings): BurndownSettings {
    const defaultSettings: BurndownSettings = {
        /** Spec required initial default values */
        burndownTrendlineEnabled: false,
        totalScopeTrendlineEnabled: settings.totalScopeTrendlineEnabled,
        completedWorkEnabled: settings.completedWorkEnabled,
        stackByWorkItemTypeEnabled: settings.stackByWorkItemTypeEnabled,
        aggregation: settings.aggregation,
        workItemTypeFilter: settings.workItemTypeFilter,
        showResolvedItemsAsCompletedEnabled: settings.showResolvedItemsAsCompletedEnabled,
        timePeriodConfiguration: {
            startDate: settings.timePeriodConfiguration.startDate,
            samplingConfiguration: {
                identifier: DateSampleMode.ByDateInterval,
                settings: {
                    endDate: settings.timePeriodConfiguration.endDate,
                    lastDayOfWeek: DayOfWeek.Friday,
                    sampleInterval: DateSampleInterval.Days
                }
            }
        },
        teams: [
            {
                teamId: settings.team && settings.team.teamId,
                projectId: settings.team && settings.team.projectId
            }
        ],
        fieldFilters: [{ fieldName: "System.IterationPath", queryOperation: "=", queryValue: settings.iterationPath }]
    } as BurndownSettings;
    return defaultSettings;
}

/** Converts a configuration with @CurrentIteration to a concrete iteration. Used immediately prior to rendering. */
export async function mapSymbolicIterationToConcrete(
    pageContext: IVssPageContext,
    savedSettings: AnalyticsSprintBurndownSettings
): Promise<AnalyticsSprintBurndownSettings> {
    let settings = savedSettings;
    if (isSymbolicCurrentIteration(savedSettings.iterationId)) {
        settings = { ...savedSettings };
        const iterations = await getIterations(pageContext, savedSettings.team.projectId, savedSettings.team.teamId);
        const activeIteration = selectActualCurrentIteration(iterations);
        settings.iterationId = activeIteration.IterationSK;
        settings.iterationPath = activeIteration.IterationPath;
        settings.timePeriodConfiguration = assembleTimePeriodConfiguration(
            parseIsoDateStringInLocalTime(activeIteration.StartDateTimeOffset),
            parseIsoDateStringInLocalTime(activeIteration.EndDateTimeOffset)
        );
    }
    return settings;
}
